/**
 * GameEndConditionChecker Service
 *
 * Централизованная проверка условий завершения игры.
 * Извлечено из God объектов для единой точки принятия решений.
 *
 * Responsibilities:
 * - Check various end game conditions
 * - Determine game winners
 * - Validate if game can continue
 * - Track elimination and reanimation possibilities
 *
 * Follows Single Responsibility Principle
 * Ограничение: < 250 строк согласно WBS_MASTER_PLAN
 */
import { TerritoryState } from '../domain/Territory';
import { MovementValidator } from '../mechanics/MovementValidator';
export var GameEndReason;
(function (GameEndReason) {
    GameEndReason["NO_ACTIVE_SIDES"] = "no-active-sides";
    GameEndReason["ONE_PLAYER_LEFT"] = "one-player-left";
    GameEndReason["NO_POSSIBLE_MOVES"] = "no-possible-moves";
    GameEndReason["ALL_TERRITORIES_CAPTURED"] = "all-territories-captured";
    GameEndReason["TIME_LIMIT_REACHED"] = "time-limit-reached";
    GameEndReason["SCORE_THRESHOLD_REACHED"] = "score-threshold-reached";
    GameEndReason["MANUAL_END"] = "manual-end";
})(GameEndReason || (GameEndReason = {}));
/**
 * GameEndConditionChecker - проверка условий завершения игры
 */
export class GameEndConditionChecker {
    board;
    scoreService;
    movementValidator;
    constructor(board, scoreService, _playerRegistry) {
        this.board = board;
        this.scoreService = scoreService;
        this.movementValidator = new MovementValidator(board);
    }
    /**
     * Комплексная проверка всех условий завершения
     */
    checkEndConditions(options = {}) {
        // Собираем информацию о сторонах
        const sidesInfo = this.analyzeSides();
        // 1. Проверка отсутствия активных сторон
        if (sidesInfo.activeSides.length === 0) {
            return {
                shouldEnd: true,
                reason: GameEndReason.NO_ACTIVE_SIDES,
                winner: null,
                isDraw: false,
                ...sidesInfo,
            };
        }
        // 2. Остался только один активный игрок
        if (sidesInfo.activeSides.length === 1) {
            return {
                shouldEnd: true,
                reason: GameEndReason.ONE_PLAYER_LEFT,
                winner: sidesInfo.activeSides[0] ?? null,
                isDraw: false,
                ...sidesInfo,
            };
        }
        // 3. Проверка возможности ходов
        const movesPossible = this.checkMovesPossible(sidesInfo);
        if (!movesPossible.anyoneCanMove && !movesPossible.reanimationPossible) {
            return {
                shouldEnd: true,
                reason: GameEndReason.NO_POSSIBLE_MOVES,
                winner: this.scoreService.getCurrentLeader(),
                isDraw: this.scoreService.isDraw(),
                ...sidesInfo,
            };
        }
        // 4. Проверка захвата всех территорий
        if (this.checkAllTerritoriesCaptured()) {
            return {
                shouldEnd: true,
                reason: GameEndReason.ALL_TERRITORIES_CAPTURED,
                winner: this.scoreService.getCurrentLeader(),
                isDraw: this.scoreService.isDraw(),
                ...sidesInfo,
            };
        }
        // 5. Проверка порога очков (если включено)
        if (options.checkScore && options.scoreThreshold) {
            const scoreResult = this.scoreService.hasReachedVictoryThreshold(options.scoreThreshold);
            if (scoreResult.reached) {
                return {
                    shouldEnd: true,
                    reason: GameEndReason.SCORE_THRESHOLD_REACHED,
                    winner: scoreResult.sideId,
                    isDraw: false,
                    ...sidesInfo,
                };
            }
        }
        // 6. Проверка временного лимита (если установлен)
        if (options.timeLimit && options.currentTime) {
            if (options.currentTime >= options.timeLimit) {
                return {
                    shouldEnd: true,
                    reason: GameEndReason.TIME_LIMIT_REACHED,
                    winner: this.scoreService.getCurrentLeader(),
                    isDraw: this.scoreService.isDraw(),
                    ...sidesInfo,
                };
            }
        }
        // Игра может продолжаться
        return {
            shouldEnd: false,
            canContinue: true,
            ...sidesInfo,
        };
    }
    /**
     * Анализ состояния сторон
     */
    analyzeSides() {
        const activeSides = [];
        const suspendedSides = [];
        const eliminatedSides = [];
        for (let sideId = 0; sideId <= 9; sideId++) {
            const side = this.board.getSide(sideId);
            if (side) {
                if (side.isActive) {
                    activeSides.push(sideId);
                }
                else if (side.isSuspended) {
                    suspendedSides.push(sideId);
                }
                else if (side.isEliminated) {
                    eliminatedSides.push(sideId);
                }
            }
        }
        return { activeSides, suspendedSides, eliminatedSides };
    }
    /**
     * Проверка возможности ходов
     */
    checkMovesPossible(sidesInfo) {
        // Проверяем, может ли кто-то ходить
        let anyoneCanMove = false;
        for (const sideId of sidesInfo.activeSides) {
            if (this.movementValidator.hasAvailableMoves(sideId)) {
                anyoneCanMove = true;
                break;
            }
        }
        // Проверяем возможность реанимации
        const reanimationPossible = this.checkReanimationPossible(sidesInfo.activeSides, sidesInfo.suspendedSides);
        return { anyoneCanMove, reanimationPossible };
    }
    /**
     * Проверка возможности реанимации
     */
    checkReanimationPossible(activeSides, suspendedSides) {
        // Реанимация возможна если:
        // 1. Есть suspended стороны
        // 2. Есть активные стороны, которые могут их реанимировать
        // 3. Есть нейтральные территории для захвата
        if (suspendedSides.length === 0) {
            return false;
        }
        if (activeSides.length === 0) {
            return false;
        }
        // Проверяем наличие нейтральных территорий
        let hasNeutralTerritories = false;
        for (const [_, territory] of this.board.territories) {
            if (territory.owner === null && territory.state !== TerritoryState.BURNED) {
                hasNeutralTerritories = true;
                break;
            }
        }
        // Реанимация возможна через ловушки, если есть нейтральные территории
        return hasNeutralTerritories;
    }
    /**
     * Проверка захвата всех территорий
     */
    checkAllTerritoriesCaptured() {
        for (const [_, territory] of this.board.territories) {
            if (territory.owner === null && territory.state !== TerritoryState.BURNED) {
                return false; // Есть незахваченная территория
            }
        }
        return true;
    }
    /**
     * Быстрая проверка - может ли игра продолжаться
     */
    canGameContinue() {
        const result = this.checkEndConditions();
        return !result.shouldEnd;
    }
    /**
     * Определение победителя по текущему состоянию
     */
    determineWinner() {
        const sidesInfo = this.analyzeSides();
        // Если остался только один активный
        if (sidesInfo.activeSides.length === 1) {
            return sidesInfo.activeSides[0] ?? null;
        }
        // Иначе по очкам
        return this.scoreService.determineWinner();
    }
}
//# sourceMappingURL=GameEndConditionChecker.js.map